import React, { useState } from 'react';
import Icon from './ui-stubs';
import { Button } from './ui-stubs';

const BulkActionsBar = ({ selectedCount, onAction, onCancel, canBulkEdit, selectedTasks, onUndo, canUndo, lastAction }) => {
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showPriorityMenu, setShowPriorityMenu] = useState(false);
  const [showOwnerMenu, setShowOwnerMenu] = useState(false);
  const [showContributorsMenu, setShowContributorsMenu] = useState(false);
  const [showTagMenu, setShowTagMenu] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedContributors, setSelectedContributors] = useState([]);

  const statusOptions = [
    { value: 'To Do', label: 'To Do', icon: 'Circle' },
    { value: 'In Progress', label: 'In Progress', icon: 'Clock' },
    { value: 'In Review', label: 'In Review', icon: 'Eye' },
    { value: 'Blocked', label: 'Blocked', icon: 'AlertCircle' },
    { value: 'Done', label: 'Done', icon: 'CheckCircle' },
  ];

  const priorityOptions = [
    { value: 'Critical', label: 'Critical', color: 'text-red-600' },
    { value: 'High', label: 'High', color: 'text-orange-600' },
    { value: 'Medium', label: 'Medium', color: 'text-yellow-600' },
    { value: 'Low', label: 'Low', color: 'text-green-600' },
  ];

  const userOptions = [
    { id: 'u1', label: 'Sarah Johnson', avatar: 'SJ', role: 'Product Designer' },
    { id: 'u2', label: 'Michael Chen', avatar: 'MC', role: 'Senior Developer' },
    { id: 'u3', label: 'Emily Rodriguez', avatar: 'ER', role: 'HR Manager' },
    { id: 'u4', label: 'David Kim', avatar: 'DK', role: 'DevOps Engineer' },
    { id: 'u5', label: 'Lisa Anderson', avatar: 'LA', role: 'UX Researcher' },
    { id: 'u6', label: 'Robert Taylor', avatar: 'RT', role: 'Marketing Designer' },
    { id: 'u7', label: 'Ahmed Al-Mansoori', avatar: 'AA', role: 'Facilities Manager' },
  ];

  const tagOptions = [
    { value: 'UX', label: 'UX' },
    { value: 'Backend', label: 'Backend' },
    { value: 'Frontend', label: 'Frontend' },
    { value: 'Security', label: 'Security' },
    { value: 'Documentation', label: 'Documentation' },
  ];

  // Check if tasks span multiple projects
  const spansMultipleProjects = () => {
    if (!selectedTasks || selectedTasks?.length === 0) return false;
    const projectIds = new Set(selectedTasks.map(task => task?.projectId));
    return projectIds?.size > 1;
  };

  // Get permission-specific message
  const getPermissionMessage = () => {
    if (canBulkEdit) return null;

    // This is a simplified example - in real implementation,
    // you'd check specific permissions
    return "You can view these tasks, but cannot change ownership or delete them.";
  };

  const handleAction = (action, value) => {
    if (!canBulkEdit && ['changeOwner', 'addContributors', 'delete']?.includes(action)) {
      return;
    }

    onAction(action, value);
    setShowStatusMenu(false);
    setShowPriorityMenu(false);
    setShowOwnerMenu(false);
    setShowContributorsMenu(false);
    setShowTagMenu(false);
    setShowDatePicker(false);
    setSelectedContributors([]);
  };

  const handleDelete = () => {
    if (!canBulkEdit) {
      return;
    }
    onAction('delete');
    setShowDeleteConfirm(false);
  };

  const handleDateSubmit = () => {
    if (selectedDate) {
      handleAction('dueDate', selectedDate);
      setSelectedDate('');
    }
  };

  const handleContributorToggle = (userId) => {
    setSelectedContributors(prev =>
      prev?.includes(userId)
        ? prev?.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleAddContributors = () => {
    if (selectedContributors?.length > 0) {
      handleAction('addContributors', selectedContributors);
    }
  };

  const getUndoLabel = () => {
    if (!lastAction) return 'Undo';

    switch (lastAction?.type) {
      case 'status':
        return `Undo: Status changed to ${lastAction?.value}`;
      case 'priority':
        return `Undo: Priority changed to ${lastAction?.value}`;
      case 'changeOwner':
        return 'Undo: Owner changed';
      case 'addContributors':
        return 'Undo: Contributors added';
      case 'addTag':
        return `Undo: Tag "${lastAction?.value}" added`;
      case 'dueDate':
        return 'Undo: Due date changed';
      default:
        return 'Undo';
    }
  };

  const permissionMessage = getPermissionMessage();

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border shadow-lg z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Selection Info */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold text-sm">
                  {selectedCount}
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {selectedCount} {selectedCount === 1 ? 'task' : 'tasks'} selected
                </span>
              </div>
              <button
                onClick={onCancel}
                className="text-sm text-gray-600 hover:text-gray-900 underline"
              >
                Clear selection
              </button>
              {canUndo && (
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Undo"
                  onClick={onUndo}
                  className="ml-2"
                  title={getUndoLabel()}
                >
                  {getUndoLabel()}
                </Button>
              )}
            </div>

            {/* Right: Action Buttons */}
            <div className="flex items-center space-x-3">
              {permissionMessage && (
                <div className="flex items-center space-x-2 text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded-md border border-amber-200 max-w-xs">
                  <Icon name="Lock" size={16} className="flex-shrink-0" />
                  <span className="text-xs">{permissionMessage}</span>
                </div>
              )}

              {/* Responsibility Group */}
              <div className="flex items-center space-x-2 border-r border-border pr-3">
                {/* Change Owner */}
                <div className="relative">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="User"
                    onClick={() => setShowOwnerMenu(!showOwnerMenu)}
                    disabled={!canBulkEdit}
                    title="Change task owner (accountability)"
                  >
                    Change Owner
                  </Button>
                  {showOwnerMenu && (
                    <div className="absolute bottom-full mb-2 right-0 w-80 bg-card border border-border rounded-lg shadow-xl">
                      <div className="px-4 py-3 border-b border-border bg-amber-50">
                        <div className="flex items-start space-x-2">
                          <Icon name="AlertTriangle" size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-semibold text-amber-900">Change Accountability</p>
                            <p className="text-xs text-amber-700 mt-1">
                              This will change accountability for all {selectedCount} selected {selectedCount === 1 ? 'task' : 'tasks'}.
                            </p>
                            {spansMultipleProjects() && (
                              <p className="text-xs text-amber-700 mt-1 font-medium">
                                ⚠️ Selection spans multiple projects
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {userOptions?.map((user) => (
                          <button
                            key={user?.id}
                            onClick={() => handleAction('changeOwner', user)}
                            className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center space-x-3 border-b border-border last:border-0"
                          >
                            <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0">
                              {user?.avatar}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900">{user?.label}</p>
                              <p className="text-xs text-gray-500">{user?.role}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Add Contributors */}
                <div className="relative">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Users"
                    onClick={() => setShowContributorsMenu(!showContributorsMenu)}
                    disabled={!canBulkEdit}
                    title="Add contributors (collaborators)"
                  >
                    Add Contributors
                  </Button>
                  {showContributorsMenu && (
                    <div className="absolute bottom-full mb-2 right-0 w-80 bg-card border border-border rounded-lg shadow-xl">
                      <div className="px-4 py-3 border-b border-border bg-blue-50">
                        <div className="flex items-start space-x-2">
                          <Icon name="Info" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-semibold text-blue-900">Add Collaborators</p>
                            <p className="text-xs text-blue-700 mt-1">
                              Contributors collaborate but are not accountable for task delivery.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {userOptions?.map((user) => (
                          <label
                            key={user?.id}
                            className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center space-x-3 border-b border-border last:border-0 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedContributors?.includes(user?.id)}
                              onChange={() => handleContributorToggle(user?.id)}
                              className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                            />
                            <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0">
                              {user?.avatar}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900">{user?.label}</p>
                              <p className="text-xs text-gray-500">{user?.role}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                      <div className="px-4 py-3 border-t border-border bg-muted flex justify-end space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowContributorsMenu(false);
                            setSelectedContributors([]);
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleAddContributors}
                          disabled={selectedContributors?.length === 0}
                        >
                          Add {selectedContributors?.length > 0 ? `(${selectedContributors?.length})` : ''}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* State Group */}
              <div className="flex items-center space-x-2 border-r border-border pr-3">
                {/* Change Status */}
                <div className="relative">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="RefreshCw"
                    onClick={() => setShowStatusMenu(!showStatusMenu)}
                    disabled={!canBulkEdit}
                  >
                    Change Status
                  </Button>
                  {showStatusMenu && (
                    <div className="absolute bottom-full mb-2 right-0 w-48 bg-card border border-border rounded-lg shadow-lg">
                      <div className="px-3 py-2 border-b border-border text-xs text-gray-600">
                        {selectedCount} {selectedCount === 1 ? 'task' : 'tasks'} will be updated
                      </div>
                      {statusOptions?.map((option) => (
                        <button
                          key={option?.value}
                          onClick={() => handleAction('status', option?.value)}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
                        >
                          <Icon name={option?.icon} size={14} />
                          <span>{option?.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Change Priority */}
                <div className="relative">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Flag"
                    onClick={() => setShowPriorityMenu(!showPriorityMenu)}
                    disabled={!canBulkEdit}
                  >
                    Change Priority
                  </Button>
                  {showPriorityMenu && (
                    <div className="absolute bottom-full mb-2 right-0 w-40 bg-card border border-border rounded-lg shadow-lg">
                      <div className="px-3 py-2 border-b border-border text-xs text-gray-600">
                        {selectedCount} {selectedCount === 1 ? 'task' : 'tasks'} will be updated
                      </div>
                      {priorityOptions?.map((option) => (
                        <button
                          key={option?.value}
                          onClick={() => handleAction('priority', option?.value)}
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${option?.color} font-medium`}
                        >
                          {option?.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Set Due Date */}
                <div className="relative">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Calendar"
                    onClick={() => setShowDatePicker(!showDatePicker)}
                    disabled={!canBulkEdit}
                  >
                    Due Date
                  </Button>
                  {showDatePicker && (
                    <div className="absolute bottom-full mb-2 right-0 w-64 bg-card border border-border rounded-lg shadow-lg p-4">
                      <div className="mb-3 text-xs text-gray-600">
                        Set due date for {selectedCount} {selectedCount === 1 ? 'task' : 'tasks'}
                      </div>
                      <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e?.target?.value)}
                        className="w-full px-3 py-2 border border-border rounded-md text-sm mb-3"
                      />
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          onClick={handleDateSubmit}
                          disabled={!selectedDate}
                        >
                          Apply
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowDatePicker(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Classification Group */}
              <div className="flex items-center space-x-2 border-r border-border pr-3">
                {/* Add Tags */}
                <div className="relative">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Tag"
                    onClick={() => setShowTagMenu(!showTagMenu)}
                    disabled={!canBulkEdit}
                  >
                    Add Tags
                  </Button>
                  {showTagMenu && (
                    <div className="absolute bottom-full mb-2 right-0 w-48 bg-card border border-border rounded-lg shadow-lg">
                      <div className="px-3 py-2 border-b border-border text-xs font-semibold text-gray-700">
                        Add Tag
                      </div>
                      {tagOptions?.map((option) => (
                        <button
                          key={option?.value}
                          onClick={() => handleAction('addTag', option?.value)}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50"
                        >
                          {option?.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Destructive Group */}
              <div className="flex items-center space-x-2">
                {/* Delete */}
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Trash2"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="text-red-600 hover:bg-red-50 border-red-200"
                  disabled={!canBulkEdit}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>

          {/* Checklist Safety Notice */}
          <div className="mt-3 flex items-start space-x-2 text-xs text-gray-500 bg-muted px-3 py-2 rounded-md border border-border">
            <Icon name="Info" size={14} className="mt-0.5 flex-shrink-0" />
            <p>
              <span className="font-medium">Checklist actions are managed inside individual tasks.</span>
              {' '}Bulk operations on checklist items, assignments, or completion status are not supported to prevent responsibility drift.
            </p>
          </div>
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Icon name="AlertTriangle" size={20} className="text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Confirm Deletion</h2>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <strong>{selectedCount}</strong> {selectedCount === 1 ? 'task' : 'tasks'}? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Delete {selectedCount} {selectedCount === 1 ? 'Task' : 'Tasks'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BulkActionsBar;
